import React, { useEffect, useState } from "react";
import { PlayCircle, X, Film } from "lucide-react";
import { VIDEO_LIST, toEmbed, ytThumb } from "../data/videos";
import { track } from "../lib/tracking";

export default function VideoStories({
  eyebrow = "Watch",
  title = "Stories from Alpha homes.",
  subtitle = "Short explainers from the people doing the work — participants, families, and our support teams.",
  videos = VIDEO_LIST,
  testid = "video-stories",
}) {
  const [active, setActive] = useState(null); // video object

  // Close on ESC
  useEffect(() => {
    if (!active) return;
    const onKey = (e) => e.key === "Escape" && setActive(null);
    document.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [active]);

  const open = (v) => {
    setActive(v);
    track("video_play", { title: v.title, url: v.url });
  };

  return (
    <section className="alpha-section-tight" data-testid={testid}>
      <div className="alpha-container">
        <div className="flex items-end justify-between gap-6 flex-wrap mb-6 md:mb-8 alpha-reveal">
          <div>
            <div className="alpha-overline"><Film className="w-3.5 h-3.5 inline -mt-0.5 mr-1.5" />{eyebrow}</div>
            <h2 className="mt-3 font-display text-3xl sm:text-[40px] font-medium tracking-tight leading-[1.05]">
              {title}
            </h2>
            <p className="mt-2 text-[color:var(--alpha-mute)] max-w-xl text-[15px]">{subtitle}</p>
          </div>
        </div>

        <div className="grid md:grid-cols-3 gap-4 md:gap-5">
          {videos.map((v, i) => {
            const poster = ytThumb(v.url) || v.poster;
            return (
              <button
                key={v.url}
                onClick={() => open(v)}
                className="group relative rounded-2xl overflow-hidden aspect-video shadow-md hover:shadow-2xl transition-shadow text-left"
                data-testid={`video-card-${i}`}
                aria-label={`Play video: ${v.title}`}
              >
                <img
                  src={poster}
                  alt={v.title}
                  loading="lazy"
                  className="absolute inset-0 w-full h-full object-cover group-hover:scale-[1.07] transition-transform duration-[1100ms]"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#2F1A5C]/90 via-[#2F1A5C]/30 to-transparent" />
                <span className="absolute top-3 left-3 text-[10px] uppercase tracking-[0.22em] bg-white/20 backdrop-blur-sm text-white px-2 py-1 rounded-full">
                  Watch · 1 min
                </span>
                <span className="absolute inset-0 flex items-center justify-center">
                  <span className="w-16 h-16 md:w-[72px] md:h-[72px] rounded-full bg-white/95 text-[color:var(--alpha-purple-700)] flex items-center justify-center shadow-xl group-hover:scale-110 transition-transform alpha-chat-pulse">
                    <PlayCircle className="w-10 h-10" strokeWidth={1.25} />
                  </span>
                </span>
                <div className="absolute inset-x-0 bottom-0 p-4 md:p-5 !text-white">
                  <div className="font-display font-semibold text-[16px] md:text-[18px] leading-snug !text-white">
                    {v.title}
                  </div>
                  <div className="text-white/80 text-xs mt-1 line-clamp-2">{v.blurb}</div>
                </div>
              </button>
            );
          })}
        </div>
      </div>

      {/* Lightbox */}
      {active && (
        <div
          className="fixed inset-0 z-[60] bg-[#1a0b3a]/90 backdrop-blur-sm flex items-center justify-center p-3 md:p-8 alpha-fade-up"
          role="dialog"
          aria-modal="true"
          data-testid="video-lightbox"
          onClick={(e) => { if (e.target === e.currentTarget) setActive(null); }}
        >
          <div className="relative w-full max-w-5xl">
            <button
              onClick={() => setActive(null)}
              aria-label="Close video"
              className="absolute -top-12 right-0 w-10 h-10 rounded-full bg-white/15 hover:bg-white/25 backdrop-blur text-white flex items-center justify-center transition-colors"
              data-testid="video-lightbox-close"
            >
              <X className="w-5 h-5" />
            </button>
            <div className="rounded-2xl overflow-hidden shadow-2xl aspect-video bg-black">
              <iframe
                title={active.title}
                src={toEmbed(active.url)}
                className="w-full h-full"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                allowFullScreen
              />
            </div>
            <div className="mt-4 text-white">
              <div className="font-display font-semibold text-lg !text-white">{active.title}</div>
              <div className="text-white/75 text-sm mt-0.5">{active.blurb}</div>
            </div>
          </div>
        </div>
      )}
    </section>
  );
}
